// @tennis-matches/services/governance — Duplikat-Analyse für Vereinsimport

/** @typedef {"new"|"redundant"|"enrichable"|"conflict"} ClubImportDuplicateStatus */
/** @typedef {"create"|"enrich"|"skip"|"force_create"} ClubImportRowAction */

const PROFILE_COMPARE_KEYS = [
  "postal_code",
  "street",
  "house_number",
  "city",
  "country",
  "website_url",
  "email",
  "phone",
  "club_short_name",
  "legal_name",
  "register_court",
  "register_number",
  "dtb_club_id",
  "btv_club_id",
  "founded_year",
];

const EXISTING_FIELD_MAP = {
  postal_code: "postalCode",
  street: "street",
  house_number: "houseNumber",
  city: "city",
  country: "country",
  website_url: "websiteUrl",
  email: "email",
  phone: "phone",
  club_short_name: "shortName",
  legal_name: "legalName",
  register_court: "registerCourt",
  register_number: "registerNumber",
  dtb_club_id: "dtbClubId",
  btv_club_id: "btvClubId",
  founded_year: "foundedYear",
};

function __asStr(v) {
  return String(v ?? "").trim();
}

function __normValue(key, value) {
  const v = __asStr(value);
  if (!v) return "";
  if (key === "website_url") {
    return v.replace(/\/+$/, "").toLowerCase();
  }
  if (key === "country") {
    const upper = v.toUpperCase();
    if (upper === "DE" || upper === "DEUTSCHLAND" || upper === "GERMANY") return "de";
    return v.toLowerCase();
  }
  if (key === "email") {
    return v.toLowerCase();
  }
  return v.toLowerCase();
}

export function clubDuplicateKey(clubName, city) {
  return `${__asStr(clubName).toLowerCase()}|${__asStr(city).toLowerCase()}`;
}

/**
 * @param {Array<Record<string, unknown>>} existingClubs
 */
export function buildClubDuplicateIndex(existingClubs) {
  const index = new Map();
  for (const club of Array.isArray(existingClubs) ? existingClubs : []) {
    const key = clubDuplicateKey(club?.clubName, club?.city);
    if (!index.has(key)) index.set(key, club);
  }
  return index;
}

/**
 * @param {Record<string, string>} row
 * @param {Record<string, unknown>} existing
 */
export function compareClubImportWithExisting(row, existing) {
  /** @type {Array<{ key: string, value: string }>} */
  const additions = [];
  /** @type {Array<{ key: string, importValue: string, existingValue: string }>} */
  const conflicts = [];

  for (const key of PROFILE_COMPARE_KEYS) {
    const importValue = __asStr(row?.[key]);
    if (!importValue) continue;

    const existingKey = EXISTING_FIELD_MAP[key] || key;
    const existingValue = __asStr(existing?.[existingKey]);
    if (!existingValue) {
      additions.push({ key, value: importValue });
      continue;
    }
    if (__normValue(key, importValue) !== __normValue(key, existingValue)) {
      conflicts.push({ key, importValue, existingValue });
    }
  }

  if (additions.length > 0) {
    return { kind: "enrichable", additions, conflicts };
  }
  if (conflicts.length > 0) {
    return { kind: "conflict", additions, conflicts };
  }
  return { kind: "redundant", additions, conflicts };
}

/**
 * @param {Record<string, string>} row
 * @param {Record<string, unknown>|null|undefined} existing
 */
export function defaultActionForDuplicateStatus(status) {
  if (status === "new") return "create";
  if (status === "enrichable") return "enrich";
  return "skip";
}

/**
 * @param {Array<Record<string, string>>} mappedRows
 * @param {Array<Record<string, unknown>>} existingClubs
 */
export function analyzeClubImportDuplicates(mappedRows, existingClubs) {
  const index = buildClubDuplicateIndex(existingClubs);
  const rows = Array.isArray(mappedRows) ? mappedRows : [];

  return rows.map((row, rowIndex) => {
    const key = clubDuplicateKey(row?.club_name, row?.city);
    const existing = index.get(key) || null;

    if (!existing) {
      return {
        rowIndex,
        row,
        status: "new",
        action: "create",
        existing: null,
        additions: [],
        conflicts: [],
      };
    }

    const analysis = compareClubImportWithExisting(row, existing);
    const status = analysis.kind;
    return {
      rowIndex,
      row,
      status,
      action: defaultActionForDuplicateStatus(status),
      existing,
      additions: analysis.additions,
      conflicts: analysis.conflicts,
      matchTenantId: __asStr(existing?.tenantId),
    };
  });
}

/**
 * @param {Array<{ action?: string, row?: Record<string, string>, matchTenantId?: string }>} analyzedRows
 */
export function buildClubImportPayload(analyzedRows) {
  const list = Array.isArray(analyzedRows) ? analyzedRows : [];
  return list.map((item) => {
    const row = item?.row && typeof item.row === "object" ? { ...item.row } : {};
    const action = __asStr(item?.action) || "skip";
    const payload = { ...row, import_action: action };
    if (action === "enrich" && item?.matchTenantId) {
      payload.match_tenant_id = item.matchTenantId;
    }
    if (action === "skip") {
      payload.skip_reason = __asStr(item?.status) || "manual";
    }
    return payload;
  });
}
